import { useCallback, useEffect, useRef, useState } from "react";
import WorkflowService from "../services/workflow.service";
import { peek, swr } from "../utils/swrCache";

// La lista de ejecuciones, cacheada entre montajes.
//
// Las vistas de workflows se desmontan al navegar —abrir una corrida desmonta
// la tabla— y sin caché cada vuelta atrás repetía una llamada que tarda
// segundos. Acá se pinta lo último que se sabía y se revalida por detrás: la
// tabla nunca vuelve a quedar en blanco.

// Cuánto se considera fresca la lista antes de volver a pedirla.
const FRESH_MS = 20000;

// Todo parámetro que cambie la respuesta tiene que estar en la clave. El efecto
// que dispara la lectura depende solo de ella, así que un filtro que falte aquí
// no solo reutiliza el resultado equivocado: además no vuelve a pedir nada al
// cambiar, porque la clave no se movió.
const cacheKey = (params) =>
  [
    "executions",
    params.account_id,
    params.project_id,
    params.workflow_id || "",
    params.agent_id || "",
    (params.status_filter || []).join("|"),
    (params.trigger_type_filter || []).join("|"),
    params.start_date || "",
    params.end_date || "",
    params.search || "",
    params.limit,
    params.offset,
    params.sort_by,
    params.sort_order,
  ].join(":");

export default function useExecutionsList(params, { enabled = true } = {}) {
  const key = enabled ? cacheKey(params) : null;
  const cached = key ? peek(key) : undefined;

  const [data, setData] = useState(cached || null);
  // Solo hay spinner cuando no hay nada que mostrar. Si ya hay datos, la
  // revalidación es silenciosa.
  const [loading, setLoading] = useState(enabled && !cached);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // El objeto de params se recrea en cada render; lo que identifica la lectura
  // es la clave, así que el efecto depende de ella y lee los params por ref.
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const run = useCallback(
    async (force) => {
      if (!key) return;
      const known = peek(key);
      setData(known || null);
      if (known) setRefreshing(true);
      else setLoading(true);
      setError(null);

      try {
        const result = await swr(
          key,
          async () => {
            const response = await WorkflowService.listExecutions(paramsRef.current);
            if (!response.success) throw new Error(response.error || "Failed to load executions");
            return response.data;
          },
          { maxAgeMs: FRESH_MS, force }
        );
        setData(result);
      } catch (err) {
        setError(err.message || "Failed to load executions");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [key]
  );

  useEffect(() => {
    run(false);
  }, [run]);

  return {
    executions: data?.executions || [],
    totalCount: data?.total_count || 0,
    loading,
    refreshing,
    error,
    reload: () => run(true),
  };
}
